import { ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import Button from 'components/commercetools-ui/atoms/button';
import FeedbackIconLayer from 'components/commercetools-ui/atoms/button/feedbackIconLayer';
import Link from 'components/commercetools-ui/atoms/link';
import Markdown from 'components/commercetools-ui/organisms/markdown';
import { SSOToken } from 'components/padi-ui/referral';
import { useAccount } from 'frontastic';
import { useFormat } from 'helpers/hooks/useFormat';
import usePath from 'helpers/hooks/usePath';
import { getUserData, getUserIdToken } from 'helpers/padi/cognito';
import { isStringHtml } from 'helpers/utils/isStringHtml';
import { getLocalStorage } from 'helpers/utils/localStorage';
import { jwtDecode } from 'jwt-decode';
import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { sdk } from 'sdk';
import { Address } from 'shared/types/account';
import countries from 'shared/types/countries.json';
import { ProjectConfig } from 'types/project-config';
import { Reference } from 'types/reference';
import useTrack from '../hooks/useTrack';

export interface PadiRedeemReferralContentProps {
  body?: string;
  ctaLabel?: string;
  projectConfig?: ProjectConfig;
  invalidReferralText?: string;
  invalidReferralLink?: Array<{
    ctaLabel: string;
    reference: Reference;
  }>;
}

const PadiRedeemReferralContent: React.FC<PadiRedeemReferralContentProps> = ({
  body,
  ctaLabel,
  invalidReferralText,
  invalidReferralLink,
}) => {
  const searchParams = useSearchParams();
  const { path } = usePath();
  const router = useRouter();
  const { account, getProjectConfig, refreshSession } = useAccount();

  const { trackRedeemReferralLink } = useTrack();
  const [userData, setUserData] = useState<object>();
  const [userSub, setUserSub] = useState<string>();
  const [idToken, setIdToken] = useState<string>();
  const [referralId, setReferralId] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const { formatMessage: formatMessageAccount } = useFormat({ name: 'account' });

  const sendRefereePayload = useCallback(
    async (idToken: string, referralId: string, userSub: string) => {
      const { setting: globalEndpoint } = await getProjectConfig('EXTENSION_PADI_APIS_GLOBAL_API_DOMAIN');
      const response = await fetch(`${globalEndpoint}/r/referral/api/redeem`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        method: 'POST',
        body: JSON.stringify({
          referrerSub: referralId,
          refereeSub: userSub,
        }),
      });
      if (response.ok) {
        return;
      }
    },
    [getProjectConfig],
  );

  const getAddress = useCallback(
    async (token: string, referralId: string) => {
      const countryCode = getLocalStorage('padiUserCountry')?.toUpperCase() || 'US';
      const country = countries.find((c) => c.twoLetterISORegionName === countryCode);
      let address: Address = {
        firstName: account?.firstName,
        lastName: account?.lastName,
        country: country && country.mappingCode ? country.mappingCode : countryCode,
      };
      const user = jwtDecode<SSOToken>(token || '');
      const affiliateId = user['custom:affiliate_id'] || '';
      const contactEndpoint = await getProjectConfig('EXTENSION_PADI_APIS_CONTACT_ENDPOINT');
      const contactRes = await fetch(`${contactEndpoint.setting}/${affiliateId}/addresses?addressType=Shipping`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (contactRes.ok) {
        const contactAddress = await contactRes.json();
        if (contactAddress && contactAddress.length > 0) {
          const { addressLine1, addressLine2, postalCode, city, stateProvinceIsoCode, countryIsoCode } =
            contactAddress[0];
          address = {
            streetName: addressLine1,
            additionalStreetInfo: addressLine2,
            postalCode: postalCode,
            city: city,
            state: stateProvinceIsoCode,
            country: countryIsoCode,
            ...address,
          };
        }
      }
      const profileEndpoint = await getProjectConfig('EXTENSION_PADI_APIS_LEARNING_PROFILE_ENDPOINT');
      const profileRes = await fetch(`${profileEndpoint.setting}/${affiliateId}`, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (profileRes.ok) {
        const profile = await profileRes.json();
        address.phone = profile.phoneNumber;
      }
      const additionalAddressInfo = JSON.stringify({
        ...address,
        email: account?.email,
        taxAddress: {
          countryName: country?.englishName,
          countryId: country?.id,
        },
      });
      address.additionalAddressInfo = additionalAddressInfo;
      return {
        email: account?.email,
        affiliateId,
        storeNumber: getLocalStorage('affiliateDiveShop') || '',
        billing: address,
        shipping: {
          state: address.state,
          country: address.country,
          additionalAddressInfo,
        },
      };
    },
    [account?.email, account?.firstName, account?.lastName, getProjectConfig],
  );

  const goToLoginPage = (lvp: string) => router.push(`/login/?lvp=${lvp}`);

  const handleButtonClick = async () => {
    setIsButtonLoading(true);
    // Authenticated.
    if (referralId && userSub && idToken) {
      handleRedemption(referralId, userSub, idToken);
    } else {
      const returnPath = path.startsWith('/') ? path.substring(1) : path;
      goToLoginPage(`${returnPath}?referralId=${referralId}`);
    }
  };

  const handleRedemption = useCallback(
    async (referralId: string, userSub: string, idToken: string) => {
      trackRedeemReferralLink(referralId, userSub);
      await sendRefereePayload(idToken, referralId, userSub);
      await refreshSession();
      const payload = await getAddress(idToken, referralId);
      payload.billing.fax = referralId;
      await sdk.callAction({
        actionName: 'cart/updateCartWithAddress',
        payload,
      });
      router.push(`/courses/open-water-diver/p/60462-1B2C/?referralId=${referralId}`);
    },
    [trackRedeemReferralLink, sendRefereePayload, refreshSession, getAddress, router],
  );

  useEffect(() => {
    const getIdToken = getUserIdToken();
    const referralIdParam = searchParams.get('referralId');
    const userData = getUserData();
    if (getIdToken) setIdToken(getIdToken);
    if (referralIdParam && referralIdParam != 'undefined') setReferralId(referralIdParam);
    if (userData) {
      refreshSession();
      setUserData(userData);
      setUserSub(userData?.sub);
    }
    setIsLoading(false);
  }, [refreshSession, searchParams]);

  return (
    <>
      {!isLoading ? (
        <div className="mx-auto max-w-2xl px-24 lg:px-32">
          {!!idToken && !referralId && invalidReferralText && (
            <div className="border-l-4 border-yellow-700  px-25 py-16">
              <div className="flex">
                <div className="shrink-0">
                  <ExclamationTriangleIcon className="size-20 text-yellow-700" aria-hidden="true" />
                </div>

                <div className="ml-12">
                  <p className="text-sm text-yellow-700">
                    <span>{invalidReferralText}</span>{' '}
                    {invalidReferralLink?.map((link) => (
                      <Link
                        key={link.ctaLabel}
                        link={link.reference}
                        className="font-medium text-padi-blue underline hover:text-padi-blue-darker"
                      >
                        {link.ctaLabel}
                      </Link>
                    ))}
                  </p>
                </div>
              </div>
            </div>
          )}
          {body && (
            <div className="prose">
              {isStringHtml(body) ? (
                <div dangerouslySetInnerHTML={{ __html: body }} />
              ) : (
                <Markdown className="text-base leading-6" markdown={body} />
              )}
            </div>
          )}
          {ctaLabel && (
            <Button
              id={!idToken ? 'redeem_referral_sign_in_click' : ''}
              className="w-auto"
              type="button"
              variant="primary"
              onClick={handleButtonClick}
              loading={isButtonLoading}
              disabled={!!idToken && !referralId}
            >
              {!idToken ? formatMessageAccount({ id: 'sign.in', defaultMessage: 'Sign in' }) : ctaLabel}
            </Button>
          )}
        </div>
      ) : (
        <FeedbackIconLayer loading={true} variant="secondary" />
      )}
    </>
  );
};

export default PadiRedeemReferralContent;
